export type OAuthConnectStatus = 'success' | 'error' | 'closed'

/** Ensures `onFinish` is invoked at most once per connect attempt. */
export function createOAuthConnectLifecycle(
  onFinish?: (status: OAuthConnectStatus) => void
) {
  let settled = false
  return {
    finish(status: OAuthConnectStatus) {
      if (settled) return
      settled = true
      onFinish?.(status)
    },
  }
}

type OAuthResult =
  | { type: 'INTEGRATION_AUTH_SUCCESS'; provider: string; result: unknown }
  | { type: 'INTEGRATION_AUTH_ERROR'; provider: string; error: string; cancelled?: boolean }

/** Same-origin completion does not depend on an opener surviving provider COOP. */
export function publishOAuthResult(state: string | null, result: OAuthResult) {
  if (!state) return
  const channel = new BroadcastChannel(`integration-oauth:${state}`)
  channel.postMessage(result)
  channel.close()
}

export function openOAuthConnectPopup({
  authUrl,
  windowName,
  provider,
  onSuccess,
  onError,
  onPopupBlocked,
  onFinish,
}: {
  authUrl: string
  windowName: string
  provider: string
  onSuccess?: (result: unknown) => void
  onError?: (message: string) => void
  onPopupBlocked?: () => void
  onFinish?: (status: OAuthConnectStatus) => void
}): Window | null {
  const { finish } = createOAuthConnectLifecycle(onFinish)
  const state = new URL(authUrl).searchParams.get('state')
  if (!state) {
    onError?.('OAuth authorization URL is missing state. Restart authorization.')
    finish('error')
    return null
  }
  const channel = new BroadcastChannel(`integration-oauth:${state}`)
  // A severed window reference can report closed while OAuth is still running.
  // Keep the attempt alive until its callback or the server's 15-minute state expiry.
  const timeout = window.setTimeout(() => {
    channel.close()
    onError?.('Authorization timed out. Restart authorization.')
    finish('closed')
  }, 15 * 60 * 1000)
  const cleanup = () => {
    window.clearTimeout(timeout)
    channel.close()
  }
  channel.onmessage = (event: MessageEvent<OAuthResult>) => {
    const result = event.data
    if (typeof result?.provider !== 'string' || result.provider.toLowerCase() !== provider.toLowerCase()) return
    if (result.type === 'INTEGRATION_AUTH_SUCCESS') {
      cleanup()
      onSuccess?.(result.result)
      finish('success')
    } else if (result.type === 'INTEGRATION_AUTH_ERROR') {
      cleanup()
      if (!result.cancelled) onError?.(result.error)
      finish(result.cancelled ? 'closed' : 'error')
    }
  }
  const width = 800
  const height = 700
  const left = window.screenX + (window.outerWidth - width) / 2
  const top = window.screenY + (window.outerHeight - height) / 2
  const authWindow = window.open(authUrl, windowName,
    `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`)
  if (!authWindow) {
    cleanup()
    onPopupBlocked?.()
    finish('error')
  }
  return authWindow
}
